import { GuaranteeDto } from "api/pricing";
import { InfoIcon } from "components/icons/Icons";
import { Toggle } from "components/forms/Toggle";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { NsKey } from "enums/NsKey";

interface BorrowerGuaranteeCardItemProps {
  guarantee: GuaranteeDto;
  isRequired?: boolean;
  isActive: boolean;
  disabled?: boolean;
  isUnavailableDueToDependencies?: boolean;
  missingDependencies?: string[]; // Liste des garanties manquantes
  toggleGuarantee: () => void;
  showGuaranteeDetails: (title: string, longDescription: string) => void;
}

export const BorrowerGuaranteeCardItem: React.FC<BorrowerGuaranteeCardItemProps> = ({
  guarantee,
  isRequired = true,
  isActive,
  disabled = false,
  isUnavailableDueToDependencies = false,
  missingDependencies = [],
  toggleGuarantee,
  showGuaranteeDetails
}) => {
  const { t } = useDynamicTranslation([NsKey.Pricing]);
  const isInteractionDisabled = disabled || isRequired || isUnavailableDueToDependencies;
  const isVisuallyDisabled = disabled || isUnavailableDueToDependencies;

  // Déterminer le message de statut
  const getStatusMessage = () => {
    if (disabled) return "Indisponible";
    if (isUnavailableDueToDependencies) {
      if (missingDependencies.length === 1) {
        return `Nécessite ${missingDependencies[0]}`;
      } else if (missingDependencies.length > 1) {
        return `Nécessite ${missingDependencies.join(' et ')}`;
      }
      return "Dépendances manquantes";
    }
    return isActive ? t('PRICING.GUARANTEES.SELECTED') : t('PRICING.GUARANTEES.NOT_SELECTED');
  };

  // Déterminer les classes CSS pour le statut
  const getStatusClasses = () => {
    if (isVisuallyDisabled) return "bg-gray-100 text-gray-400";
    return isActive ? "bg-lime-100 text-lime-500" : "bg-red-100 text-red-500";
  };

  return (
    <div className={`border flex bg-white rounded-2xl transition-all duration-200 ${isVisuallyDisabled
        ? 'border-gray-100 bg-gray-50 opacity-60'
        : isActive
          ? 'border-lime-500'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}>
      <div className="grow flex items-center p-4 gap-6">
        <Toggle
          value={isActive}
          disabled={isInteractionDisabled}
          onChange={() => !isInteractionDisabled && toggleGuarantee()}
        />
        <div className="grow">
          <h3 className={`font-semibold ${isVisuallyDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
            {guarantee.name}
          </h3>
          <p className={`text-sm ${isVisuallyDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
            {guarantee.shortDescription}
          </p>
        </div>
      </div>
      <div className="flex-none w-44 p-2 gap-4 flex flex-col justify-between items-end">
        <div className={`text-xs rounded-full py-1 px-3 whitespace-nowrap ${getStatusClasses()}`}>
          {getStatusMessage()}
        </div>
        <div
          onClick={() =>
            !isVisuallyDisabled &&
            guarantee.longDescription &&
            guarantee.name &&
            showGuaranteeDetails(guarantee.name, guarantee.longDescription)
          }
          className={isVisuallyDisabled ? "cursor-not-allowed" : "cursor-pointer"}
        >
          <InfoIcon
            className={`w-5 h-5 -mb-1 ${isVisuallyDisabled ? 'text-gray-400' : 'text-primary'}`}
          />
        </div>
      </div>
    </div>
  );
};
